import * as pageActions from 'client/actions/pageActions'
import { Select } from 'client/components/core/Sidebar/components/Settings/components'
import { SettingsProps } from 'client/components/core/Sidebar/components/Settings/entities/index'
import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { UrlRedirect } from 'client/components/core/Sidebar/components/Settings/options'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { usePage } from 'client/store'
import { toggleAutomationRulePreventSaving } from 'client/store/automationRules/automationRulesActions'
import pageSelectors from 'client/store/page/pageSelectors'
import { blogPageTypes, pageTypes } from 'common/constants/pageTypes'
import {
  RedirectionTypesEnum,
  sendFormRedirectionChoices,
} from 'common/enums/ButtonActionEnum'
import { ButtonActionTypeEnum } from 'common/enums/ButtonActionTypeEnum'
import { useActions } from 'common/hooks/useActions'
import { addUrlSchemaToTheLink } from 'common/utils/urlUtils'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { usePreserveEntityFieldsAcrossDiscriminator } from 'src/editor/hooks/use-preserve-fields-across-discriminator/use-preserve-entity-fields-across-discriminator'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { pagesWithAutomationRulesTypes } from 'src/editor/shared/constants/automation-rules'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import { AutomationRulesSettings } from 'src/editor/shared/settings/automation-rules-settings'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { AutomationRuleTriggersEnum } from 'src/editor/shared/types/automation-rules/triggers-types'
import { ActionButtonEntityInterface } from 'src/shared/entities/button/action-button.schema'
import {
  SendFormActionButtonCustomUrlEntityInterface,
  SendFormActionButtonEntityInterface,
  SendFormActionButtonNoneEntityInterface,
} from 'src/shared/entities/button/send-form.action-button.schema'

export const ButtonSendFormActionSettings = ({
  entity,
}: SettingsProps<ActionButtonEntityInterface>) => {
  const dispatch = useDispatch()
  const updateProp = useUpdateProp(
    entity as SendFormActionButtonEntityInterface,
  )
  const redirectionNoneUpdateProp = useUpdateProp(
    entity as SendFormActionButtonNoneEntityInterface,
  )
  const redirectionCustomUrlUpdateProp = useUpdateProp(
    entity as SendFormActionButtonCustomUrlEntityInterface,
  )
  const pageType = usePage(pageSelectors.getPageType)
  const { isWebsite, isTemplate } = usePageRouteDetector()
  const doubleOptIn = usePage(pageSelectors.getDoubleOptIn)
  const toggleDoubleOptInAction = useActions(pageActions.toggleDoubleOptIn)

  const {
    text: { paragraphTextColor },
  } = useThemeColor()

  usePreserveEntityFieldsAcrossDiscriminator({
    entity: entity as SendFormActionButtonNoneEntityInterface,
    discriminator: [
      { key: 'action', value: ButtonActionTypeEnum.SendForm },
      { key: 'redirectionType', value: RedirectionTypesEnum.none },
    ],
    preservedKeys: [
      'redirectionType',
      'optInSuccessText',
      'optInSuccessTextColor',
    ],
  })

  usePreserveEntityFieldsAcrossDiscriminator({
    entity: entity as SendFormActionButtonCustomUrlEntityInterface,
    discriminator: [
      { key: 'action', value: ButtonActionTypeEnum.SendForm },
      { key: 'redirectionType', value: RedirectionTypesEnum.customUrl },
    ],
    preservedKeys: ['redirectionType', 'urlRedirect'],
  })

  const redirectionTypeNames = { ...sendFormRedirectionChoices }
  if (blogPageTypes.includes(pageType) || isWebsite) {
    delete redirectionTypeNames[RedirectionTypesEnum.nextStep]
  }

  const automationTriggerType = blogPageTypes.includes(pageType)
    ? AutomationRuleTriggersEnum.blog_page_form_subscribed
    : AutomationRuleTriggersEnum.form_subscribed

  const isAutomationRulesVisible =
    !isTemplate && pagesWithAutomationRulesTypes.includes(pageType)

  useEffect(() => {
    if (
      entity.action === ButtonActionTypeEnum.SendForm &&
      (!entity.redirectionType ||
        (entity.redirectionType === RedirectionTypesEnum.nextStep &&
          (blogPageTypes.includes(pageType) || isWebsite)))
    ) {
      updateProp('redirectionType')(RedirectionTypesEnum.none)
    }
  }, [entity.action, 'redirectionType' in entity && entity.redirectionType])

  useEffect(() => {
    const needResetAutomationRules =
      entity.action !== ButtonActionTypeEnum.SendForm || isWebsite
    dispatch(
      toggleAutomationRulePreventSaving({
        entityId: entity.id,
        triggerKey: 'buttonId',
        automationTriggerType,
        preventSaving: needResetAutomationRules,
      }),
    )
  }, [entity.action])

  return entity.action === ButtonActionTypeEnum.SendForm ? (
    <>
      <Select
        labelText="entity_settings.button.send_form.redirection.label"
        value={entity.redirectionType}
        update={updateProp('redirectionType')}
        dataTestElement="button-redirection-checkbox"
        choiceList={redirectionTypeNames}
      />
      {entity.redirectionType === RedirectionTypesEnum.none && (
        <>
          <TextInputControl
            wrapperProps={{
              labelText:
                'entity_settings.button.send_form.no_redirection.title',
            }}
            value={entity.optInSuccessText}
            update={redirectionNoneUpdateProp('optInSuccessText')}
          />
          <ColorPickerControl
            label="text color"
            update={redirectionNoneUpdateProp('optInSuccessTextColor')}
            color={entity.optInSuccessTextColor || paragraphTextColor}
          />
        </>
      )}
      {entity.redirectionType === RedirectionTypesEnum.customUrl && (
        <UrlRedirect
          key="urlRedirect"
          urlRedirect={entity.urlRedirect}
          update={(value: string) =>
            redirectionCustomUrlUpdateProp('urlRedirect')(
              addUrlSchemaToTheLink(value),
            )
          }
          data-test-element="button-redirection-input"
        />
      )}
      {!isWebsite && pageType !== pageTypes.webinarRegistration && (
        <CheckboxControl
          label="entity_settings.button.send_model.double_opt_in.label"
          value={doubleOptIn}
          update={toggleDoubleOptInAction}
        />
      )}
      {isAutomationRulesVisible && (
        <OptionWrapper
          labelText="entity_settings.automation_rule.automations"
          className="static"
        >
          <AutomationRulesSettings
            entityId={entity.id}
            triggerKey={'buttonId'}
            triggerType={automationTriggerType}
          />
        </OptionWrapper>
      )}
    </>
  ) : null
}
